import { useRef } from 'react';
import { notification } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AuthorizeOAuthIntegrationInput,
  OAuth2ThirdPartyEnum,
  useCacheOAuthAuthorizationCodeMutation,
  useExchangeOAuthAuthorizationCodeForTokensMutation,
  useExchangeOAuthAuthorizationCodeForTokensViaTransientTokenMutation,
  useLogOAuthAuthorizationErrorMutation,
} from '@/types/generated/graphql';
import {
  SALESFORCE_PENDING_INTEGRATION_ID_KEY,
  TRANSIENT_TOKEN,
  ZOHO_THIRD_PARTY_SLUG,
} from '../constants/integration.constants';
import { cacheAddIntegration, cacheIncrementIntegrationCount } from '../integration-handler';
import { ExecuteMutationOptions } from '../integrations.type';
import { useIntegrateIntegration } from './use-integrate-integration';
import { useModal } from '@shared/hooks/use-modal';
import Cookies from 'js-cookie';
import { cookieOptions } from '@shared/utils';
import { useTranslation } from 'react-i18next';

// Maps the lowercase `thirdPartyName` URL slug (as built by each provider's own OAuth2Client
// redirect URI, e.g. HubspotOAuth2Client's `?thirdPartyName=hubspot`) to the exact
// OAuth2ThirdPartyEnum value the GraphQL schema expects. Deliberately explicit rather than derived
// via a word-boundary `capitalize()` call: that happens to reproduce `Pipedrive` (a single word, so
// capitalizing its first letter is the whole job) but can never reproduce `HubSpot`'s internal
// capital S from the single lowercase word `hubspot` — confirmed live: the backend rejected
// `capitalize('hubspot')` === `"Hubspot"` with `VALIDATION_INVALID_TYPE_VARIABLE` since it isn't a
// member of `OAuth2ThirdPartyEnum`. `HubSpot`/`Salesforce`/`Zoho` aren't in this file's generated
// `OAuth2ThirdPartyEnum` yet (codegen hasn't been run against a schema snapshot that has them) —
// cast as raw literals until `pnpm generate` picks them up.
const THIRD_PARTY_NAME_BY_SLUG: Record<string, OAuth2ThirdPartyEnum> = {
  pipedrive: OAuth2ThirdPartyEnum.Pipedrive,
  pabblyconnect: OAuth2ThirdPartyEnum.PabblyConnect,
  hubspot: 'HubSpot' as OAuth2ThirdPartyEnum,
  [ZOHO_THIRD_PARTY_SLUG]: 'Zoho' as OAuth2ThirdPartyEnum,
  salesforce: 'Salesforce' as OAuth2ThirdPartyEnum,
};

// Zoho reports the user's data center as the `accounts-server` callback param; the backend needs it
// to pick the token endpoint. The generated `AuthorizeOAuthIntegrationInput` lacks `accountsServer`
// until `pnpm generate` runs against a schema that has it — remove this type once regenerated.
type ExchangeOAuthInput = AuthorizeOAuthIntegrationInput & { accountsServer?: string };

const resolveThirdPartyName = (slug: string | null): OAuth2ThirdPartyEnum =>
  THIRD_PARTY_NAME_BY_SLUG[(slug || 'pipedrive').toLowerCase()] ?? OAuth2ThirdPartyEnum.Pipedrive;

// Query params that only exist to carry the OAuth error redirect (see `handleOAuthAuthorizationError`
// below) — stripped from the URL once read so a refresh can't re-log the error or reopen the popup.
const OAUTH_ERROR_PARAMS = ['error', 'error_description', 'error_uri', 'state', 'thirdPartyName'];

const executeMutation = async <TInput>({ canRun, input, mutation, isRunningRef }: ExecuteMutationOptions<TInput>) => {
  if (!canRun || isRunningRef.current) return;
  isRunningRef.current = true;
  await mutation({ variables: { input } });
};

export const useThirdPartyOAuth = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRunningRef = useRef(false);
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');
  const errorURI = searchParams.get('error_uri');
  const thirdPartyNameSlug = (searchParams.get('thirdPartyName') || '').toLowerCase();
  const thirdPartyName = resolveThirdPartyName(thirdPartyNameSlug);
  const isZohoCallback = thirdPartyNameSlug === ZOHO_THIRD_PARTY_SLUG;
  const accountsServer = searchParams.get('accounts-server');
  const transientToken = Cookies.get(TRANSIENT_TOKEN);
  const { t } = useTranslation();

  const salesforcePackageNotInstalledModal = useModal();
  const pendingSalesforceIntegrationId =
    Number(sessionStorage.getItem(SALESFORCE_PENDING_INTEGRATION_ID_KEY)) || undefined;
  const { integrate: retrySalesforceIntegrate, integrating: retryingSalesforceConnection } =
    useIntegrateIntegration(pendingSalesforceIntegrationId);

  const navigateToIntegrations = () => {
    navigate('/settings/integrations?tab=all');
    isRunningRef.current = false;
  };

  const [logOAuthAuthorizationError] = useLogOAuthAuthorizationErrorMutation();

  const [cacheOAuthAuthorizationCode] = useCacheOAuthAuthorizationCodeMutation({
    onCompleted: (data) => {
      Cookies.set(
        TRANSIENT_TOKEN,
        data?.cacheOAuthAuthorizationCode?.data?.transientToken || '',
        { ...cookieOptions, expires: 5 / (60 * 24) } //transient Token expires for 5 min
      );
      notification.info({
        message: t('please_login_to_complete_pipedrive_integration'),
      });
    },
    onError: (err) => {
      notification.error({ message: err?.message });
    },
  });

  const [exchangeOauthForToken] = useExchangeOAuthAuthorizationCodeForTokensMutation({
    onCompleted: (data) => {
      notification.success({ message: data?.exchangeOAuthAuthorizationCodeForTokens?.message });
      navigateToIntegrations();
    },
    onError: (err) => {
      // Without `accounts-server` the backend falls back to Zoho's default (US) data center, so a
      // non-US account fails with a generic error; point the user at the likely cause
      const isZohoDataCenterMissing = isZohoCallback && !accountsServer;
      notification.error({
        message: err?.message,
        ...(isZohoDataCenterMissing && { description: t('zoho_data_center_missing_reconnect') }),
      });
      navigateToIntegrations();
    },
    update: (_, { data }) => {
      const mutationData = data?.exchangeOAuthAuthorizationCodeForTokens?.data;
      const integartionId = data?.exchangeOAuthAuthorizationCodeForTokens?.data?.id;
      cacheIncrementIntegrationCount(mutationData);
      cacheAddIntegration(integartionId);
    },
  });

  const [exchangeViaTransientToken] = useExchangeOAuthAuthorizationCodeForTokensViaTransientTokenMutation({
    onCompleted: (data) => {
      navigateToIntegrations();
      Cookies.remove(TRANSIENT_TOKEN, cookieOptions);
      notification.success({
        message: data?.exchangeOAuthAuthorizationCodeForTokensViaTransientToken?.message,
      });
    },
    onError: (err) => {
      Cookies.remove(TRANSIENT_TOKEN, cookieOptions);
      notification.error({ message: err?.message });
      navigateToIntegrations();
    },
  });

  const exchangeInput: ExchangeOAuthInput = {
    thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
    code: code || '',
    ...(state && { state }),
    ...(isZohoCallback && accountsServer && { accountsServer }),
  };

  const handleExchangeOauthForToken = () =>
    executeMutation({
      canRun: !!code,
      input: exchangeInput,
      mutation: exchangeOauthForToken,
      isRunningRef,
    });

  const cleanOAuthErrorParamsFromUrl = () => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        OAUTH_ERROR_PARAMS.forEach((key) => params.delete(key));
        return params;
      },
      { replace: true }
    );
  };

  // Handles every `error=` OAuth redirect (any provider): logs it once, cleans the URL so a
  // refresh can't repeat it, then reacts — Salesforce's `OAUTH_EC_APP_NOT_FOUND` opens the
  // "package not installed" popup, `access_denied` and everything else show a toast.
  const handleOAuthAuthorizationError = () => {
    if (!error || isRunningRef.current) return;
    isRunningRef.current = true;

    // The backend can't log an error it can't tie back to an authorization attempt.
    if (state) {
      logOAuthAuthorizationError({
        variables: {
          input: {
            thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
            error,
            errorDescription,
            errorURI,
            state,
          },
        },
      }).catch(() => {
        // Logging is best-effort — it must never block showing the popup/toast below.
      });
    }

    cleanOAuthErrorParamsFromUrl();

    if (thirdPartyNameSlug === 'salesforce' && error === 'OAUTH_EC_APP_NOT_FOUND') {
      salesforcePackageNotInstalledModal.handleOpen();
    } else if (error === 'access_denied') {
      notification.info({
        message: t('{provider}_connection_was_cancelled_you_can_connect_again_at_any_time', {
          provider: thirdPartyName,
        }),
      });
    } else {
      notification.error({
        message: t(
          'we_couldnt_connect_to_{provider}_{description}_please_try_again_or_contact_calilio_support_if_the_problem_continues',
          { provider: thirdPartyName, description: errorDescription || '' }
        ),
      });
    }
  };

  // Reuses the same integrate-and-redirect flow as "Continue to Salesforce" (`use-salesforce.ts`),
  // aimed at the integration the user was trying to connect when the error redirect happened.
  const retrySalesforceConnection = async () => {
    const res = await retrySalesforceIntegrate();
    const authorizationUrl = res?.data?.integrate?.data?.authorizationUrl;
    if (authorizationUrl) {
      window.open(authorizationUrl, '_self');
    }
    salesforcePackageNotInstalledModal.handleClose();
  };

  const handleCacheOAuth = () =>
    executeMutation({
      canRun: !!code,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        code: code || '',
      },
      mutation: cacheOAuthAuthorizationCode,
      isRunningRef,
    });

  const handleExchangeViaTransientToken = () =>
    executeMutation({
      canRun: !!transientToken,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        transientToken,
      },
      mutation: exchangeViaTransientToken,
      isRunningRef,
    });

  return {
    thirdPartyOAuthCode: code,
    handleExchangeOauthForToken,
    handleOAuthAuthorizationError,
    handleCacheOAuth,
    handleExchangeViaTransientToken,
    salesforcePackageNotInstalledModal,
    retrySalesforceConnection,
    retryingSalesforceConnection,
  };
};
